import { LogOut, RotateCcw, ShieldOff, TriangleAlert, UserX } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { Button } from '@/components/ui/button';

import { fetchOwnInvitation } from '../api';
import { CurrentUserContext, useAuth, type CurrentUser } from '../auth-context';
import { useProfile } from '../hooks/use-profile';

import { AuthSplash, FullPageState } from './full-page-state';

/**
 * Gate for the private application. Renders nothing private until the session
 * AND an active profile are confirmed, so the shell never flashes for anonymous
 * visitors during session restore.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, session, signOut } = useAuth();
  const location = useLocation();
  const profileQuery = useProfile(session?.user.id);
  const profile = profileQuery.data;
  // Inactive accounts may simply not have finished their invitation yet.
  const invitationQuery = useQuery({
    queryKey: ['own-invitation', session?.user.id ?? 'anonymous'],
    queryFn: () => fetchOwnInvitation(session?.user.id ?? ''),
    enabled: session !== null && profile?.is_active === false,
  });

  const currentUser = useMemo<CurrentUser | null>(
    () => (session && profile ? { session, profile } : null),
    [session, profile],
  );

  if (status === 'loading') return <AuthSplash />;

  if (status === 'unauthenticated' || !session) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const logoutButton = (
    <Button variant="secondary" onClick={() => void signOut()}>
      <LogOut aria-hidden="true" />
      Cerrar sesión
    </Button>
  );

  if (profileQuery.isPending) return <AuthSplash label="Cargando perfil…" />;

  if (profileQuery.isError) {
    return (
      <FullPageState
        tone="danger"
        icon={TriangleAlert}
        title="No se pudo cargar tu perfil"
        description="Hubo un problema al verificar tu acceso. Revisa tu conexión e inténtalo nuevamente."
        actions={
          <>
            <Button onClick={() => void profileQuery.refetch()} loading={profileQuery.isFetching}>
              {!profileQuery.isFetching && <RotateCcw aria-hidden="true" />}
              Reintentar
            </Button>
            {logoutButton}
          </>
        }
      />
    );
  }

  if (!profile || !currentUser) {
    return (
      <FullPageState
        icon={UserX}
        title="Tu cuenta no tiene un perfil asignado"
        description="Un administrador debe completar la configuración de tu acceso antes de que puedas usar el ERP."
        actions={logoutButton}
      />
    );
  }

  if (!profile.is_active) {
    if (invitationQuery.isPending) return <AuthSplash label="Cargando perfil…" />;
    const invitation = invitationQuery.data;
    if (invitation && !invitation.accepted_at && !invitation.cancelled_at) {
      return <Navigate to="/aceptar-invitacion" replace />;
    }
    return (
      <FullPageState
        icon={ShieldOff}
        title="Acceso deshabilitado"
        description="Tu cuenta está desactivada. Si crees que es un error, contacta a un administrador de SORAI."
        actions={logoutButton}
      />
    );
  }

  return <CurrentUserContext.Provider value={currentUser}>{children}</CurrentUserContext.Provider>;
}
